<template>
  <Card :title="getTitle()" logo="elasticsearch">
    <a :href="docs" target="_blank" v-if="showMagentoDocs()">
      {{ $t("global.elasticsearch-with-magento") }}
    </a>
    <CardActionContent :buttons="getButtons()">
      <template v-if="container" #description>
        <LoadingState v-if="isLoading()" class="mb-2" />
        <CardListContent v-else :list="getInfo()" />
      </template>
    </CardActionContent>
  </Card>
</template>

<script>
import Card from "@/components/display/Card";
import CardActionContent from "@/components/display/card-sub-components/CardActionContent";
import CardListContent from "@/components/display/card-sub-components/CardListContent";
import { computed } from "@vue/composition-api";
import ElasticSearchDisableModal from "@/components/smart/modals/ElasticSearchDisable";
import ElasticSearchEnableModal from "@/components/smart/modals/ElasticSearchEnable";
import LoadingState from "@/components/display/LoadingState";
import NavLink from "@/classes/NavLink";
import store from "@/store";

export default {
  name: "ElasticSearch",
  components: {
    CardActionContent,
    LoadingState,
    Card,
    CardListContent,
  },
  props: {
    site: Object,
  },
  setup(props, { root }) {
    const container = computed(() => store.getters["containers/getElasticSearch"]);
    const isLoading = () => container.value && !container.value.isStable;

    // name and version of container
    // version is only included if elasticsearch has been enabled
    const getTitle = () => {
      if (!container.value) {
        return root.$t("global.elasticsearch");
      }
      return `${root.$t("global.elasticsearch")} ${container.value.version}`;
    };

    // list of hostname (endpoint) and port used by elasticsearch
    const getInfo = () => {
      // if no container, or is still loading, return empty list
      if (!container.value) {
        return [];
      }

      // else return list filled with elasticsearch info
      return [
        {
          label: root.$t("global.endpoint"),
          content: container.value.hostname,
          canCopy: true,
        },
        {
          label: root.$t("global.port"),
          content: container.value.ports[0].publishPort,
          canCopy: true,
        },
      ];
    };

    // shows the enable elasticsearch modal
    const enable = () => {
      store.dispatch("modals/open", {
        template: ElasticSearchEnableModal,
        data: { site: props.site },
      });
    };

    // shows the disable elasticsearch modal
    const disable = () => {
      store.dispatch("modals/open", {
        template: ElasticSearchDisableModal,
        data: { site: props.site, container: container.value },
      });
    };

    const getButtons = () => {
      // if an elasticsearch container exists, but is not stable, don't show any buttons
      if (container.value && !container.value.isStable) {
        return [];
      }

      // if elasticsearch has been enabled, show the disable button
      if (container.value) {
        return [new NavLink(root.$t("global.disable"), null, disable, false, "danger")];
      }

      // ... else show the enable button
      return [new NavLink(root.$t("global.enable"), null, enable, false, "primary")];
    };

    // link to magento docs regarding its elasticsearch dependency
    const docs =
      "https://devdocs.magento.com/guides/v2.3/config-guide/elasticsearch/configure-magento.html";

    const showMagentoDocs = () => props.site.app.type === "magento";

    return {
      docs,
      container,
      getButtons,
      getTitle,
      getInfo,
      isLoading,
      showMagentoDocs,
    };
  },
};
</script>
